import {
  FormControl,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useMemo, useState, useEffect } from "react";

import {
  BorderTableCell,
  Button,
  Modal,
  Select,
  StyledTableContainer,
} from "../../../../../../components/base";
import { COLORS } from "../../../../../../theme/colors";
import { ModalBox } from "../../../../../../components/boxes";
import { ModalTitle } from "../../../../../../components/base/Modal";
import { RedAsterisk } from "../../../../../../components/characters";
import { ArrowNumberInput } from "../../../../../../components/base/TextInput";
import { MessageAlert } from "../../../../../../components/alerts/MessageAlert";

const useStyles = makeStyles((theme) => ({
  subTitleContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    "& p:first-child": {
      fontStyle: "italic",
      color: COLORS.darks[0][50],
    },
  },
  selectContainer: {
    width: 200,
    marginBottom: theme.spacing(2),
  },
  selectorRoot: {
    padding: 10,
  },
  alertContainer: {
    backgroundColor: "#FCF3D9",
    padding: theme.spacing(1.5),
    borderRadius: 4,
  },
  alertIcon: {
    color: COLORS.orange,
    marginRight: theme.spacing(1),
  },
}));

export const DiscountRateModal = ({
  open,
  onClose,
  initialDiscounts,
  setInitialDiscountRate,
  onSave,
}) => {
  const classes = useStyles();

  const [selectedParam, setSelectedParam] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);

  const savedDiscounts = useMemo(() => {
    for (let i = 0; i < initialDiscounts.length; i++) {
      const discount = initialDiscounts[i];
      for (const v of discount.values) {
        if (v.discount_rate > 0) {
          return {
            i,
            type: discount.type,
            values: discount.values,
          };
        }
      }
    }
  }, [initialDiscounts]);

  useEffect(() => {
    if (savedDiscounts) {
      setTableData(savedDiscounts.values);
      setSelectedParam(savedDiscounts.i);
    } else if (initialDiscounts.length > 0) {
      setTableData(initialDiscounts[selectedParam]?.values);
    }
  }, [savedDiscounts, selectedParam, initialDiscounts]);

  const onChangeSelectedParam = (param) => {
    if (savedDiscounts) {
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
    setSelectedParam(param);
    setTableData(initialDiscounts[param]?.values);
  };

  const onChangeValue = (index, v) => {
    tableData[index]["discount_rate"] = v;
    setTableData([...tableData]);
    setInitialDiscountRate((prev) => {
      return prev.map((val, valueIndex) => {
        if (valueIndex === selectedParam) {
          return {
            ...val,
            discount_rate: v,
          };
        } else {
          return val;
        }
      });
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox maxWidth="md" width={600}>
        {showAlert && (
          <MessageAlert
            text="Note - only one parameter’s discounts can be saved"
            topOffset={80}
            textStyle={{ fontSize: 14 }}
          />
        )}
        <ModalTitle title="Discount Rates" onClose={onClose} />
        <Grid className={classes.subTitleContainer} container>
          <Typography>How would you like to set the discount rates?</Typography>
          <RedAsterisk />
        </Grid>

        <FormControl className={classes.selectContainer} variant="outlined">
          <Select
            rootStyle={classes.selectorRoot}
            options={Array.from({ length: initialDiscounts.length }, (_, i) => i)}
            value={selectedParam}
            onChange={onChangeSelectedParam}
            optionNames={initialDiscounts.map((d) => `By ${d.type.toLowerCase()}`)}
            downArrow
          />
        </FormControl>

        <StyledTableContainer style={{ marginBottom: 10 }}>
          <Table>
            <TableHead>
              <TableRow>
                <BorderTableCell style={{ textTransform: "capitalize" }}>
                  {initialDiscounts[selectedParam]?.type?.toLowerCase()}
                </BorderTableCell>
                <BorderTableCell>Discount rate (%)</BorderTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((rate, index) => (
                <TableRow key={index}>
                  <BorderTableCell>
                    <Typography>{rate.name}</Typography>
                  </BorderTableCell>
                  <BorderTableCell>
                    <ArrowNumberInput
                      value={rate.discount_rate + ""}
                      onChange={(v) => onChangeValue(index, v)}
                      unit="%"
                    />
                  </BorderTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <Grid container style={{ marginBottom: 18 }}>
          <RedAsterisk />
          <Typography>Discounts can be set only by a single parameter</Typography>
        </Grid>

        <Button
          text="Save"
          onClick={() => {
            onClose();
            onSave({
              type: initialDiscounts[selectedParam].type,
              values: tableData,
            });
          }}
        />
      </ModalBox>
    </Modal>
  );
};
